import React, { type FC } from 'react'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_SECONDARY,
  Button,
  Flex,
  FormElement,
  MESSAGE_BOX_VARIANT_HELP,
  MessageBox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Select,
} from 'mailup-web-components'

import { INPUT_MESSAGES_MAX_ITEMS } from 'Constants'

import useAsyncSelect from 'Hooks/useAsyncSelect/useAsyncSelect'

import {
  modalSendConfirmationCancelCallback,
  modalSendConfirmationChangeMessageCallback,
  modalSendConfirmationConfirmCallback,
} from 'Pages/RecipientsList/recoil/callbacks/modalSendConfirmation'
import {
  modalSendConfirmationHasMessageFeedback,
  modalSendConfirmationState,
  modalSendConfirmationSubmitDisabledState,
} from 'Pages/RecipientsList/recoil/state/modalSendConfirmation'

import {
  fetchMessageById,
  fetchMessages,
} from 'Pages/RecipientsList/recoil/services/asyncSelectConfirmationMessages'

const ModalSendConfirmation: FC = () => {
  const intl = useIntl()

  const { show, idConfirmationMessage, contact } = useRecoilValue(
    modalSendConfirmationState,
  )

  const showFeedback = useRecoilValue(modalSendConfirmationHasMessageFeedback)

  const submitDisabled = useRecoilValue(
    modalSendConfirmationSubmitDisabledState,
  )

  const handleChangeMessage = useRecoilCallback(
    modalSendConfirmationChangeMessageCallback,
    [],
  )

  const { options, loading, handleSearch, hint, hintNoResults } =
    useAsyncSelect({
      key: 'modal-send-confirmation',
      value: idConfirmationMessage,
      maxItems: INPUT_MESSAGES_MAX_ITEMS,
      fetchItems: async () => await fetchMessages(''),
      fetchItemsByTerm: fetchMessages,
      fetchItemById: fetchMessageById,
      labels: {
        loading: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusLoading',
        }),
        noResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusNoMatches',
        }),
        moreResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusTooManyItems',
        }),
      },
    })

  const handleCancel = useRecoilCallback(
    modalSendConfirmationCancelCallback,
    [],
  )
  const handleConfirm = useRecoilCallback(
    modalSendConfirmationConfirmCallback,
    [],
  )

  return (
    <Modal
      show={show}
      onClose={handleCancel}
      closeOnOverlayClick
      data-mu-test="modal-pending"
    >
      <ModalHead
        title={intl.formatMessage(
          {
            id: 'Load.RecipientsList.ModalSendConfirmationTitle',
          },
          {
            CONTACT: contact,
          },
        )}
      />
      <ModalBody>
        <Flex flexDirection="column" gap="24px">
          <FormElement
            required
            label={intl.formatMessage({
              id: 'Load.RecipientsList.ModalSendConfirmationInputMessage',
            })}
            variant={showFeedback ? 'danger' : undefined}
            feedback={
              showFeedback
                ? intl.formatMessage({ id: 'Load.MailupCommon.Required' })
                : undefined
            }
          >
            <Select
              data-mu-test="input-message"
              value={idConfirmationMessage}
              options={options}
              loading={loading}
              onChange={handleChangeMessage}
              onSearch={handleSearch}
              hint={hint}
              hintNoResults={hintNoResults}
              memoSelectOption
              error={showFeedback}
              placeholder={intl.formatMessage({
                id: 'Load.RecipientsList.ModalSendConfirmationSelectMessage',
              })}
            />
          </FormElement>

          <MessageBox variant={MESSAGE_BOX_VARIANT_HELP}>
            {intl.formatMessage({
              id: 'Load.RecipientsList.ModalSendConfirmationInfoMessage',
            })}
          </MessageBox>
        </Flex>
      </ModalBody>
      <ModalFoot>
        <Button
          data-mu-test="action-cancel"
          kind={BUTTON_VARIANT_SECONDARY}
          onClick={handleCancel}
        >
          {intl.formatMessage({
            id: 'Load.MailupCommon.Cancel',
          })}
        </Button>
        <Button
          data-mu-test="action-confirm"
          disabled={submitDisabled}
          kind={BUTTON_VARIANT_PRIMARY}
          onClick={handleConfirm}
        >
          {intl.formatMessage({
            id: 'Load.MailupCommon.Send',
          })}
        </Button>
      </ModalFoot>
    </Modal>
  )
}

export default ModalSendConfirmation
